import React, { useState } from 'react';

const ContactNowPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    urgency: '',
    preferred_contact: '',
    preferred_time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/urgent-contact', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          urgency: '',
          preferred_contact: '',
          preferred_time: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-500 to-red-400 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/8ccf79235cf2414040c65e7b7317b0d7.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight">
              今すぐお問い合わせください！<br />
              <span className="text-red-200 font-extrabold">緊急の経営課題を解決</span>
            </h1>
            <p className="text-2xl text-red-100 leading-relaxed font-medium max-w-4xl mx-auto">
              <span className="font-bold text-3xl text-white bg-red-600 px-3 py-1 rounded">24時間以内</span>にご連絡<br />
              緊急性の高い経営課題に迅速対応いたします
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#urgent-form" 
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                🚨 緊急お問い合わせ
              </a>
              <a 
                href="tel:03-1234-5678" 
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📞 今すぐ電話する
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Situations Section */}
      <section className="py-16 bg-red-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            こんな<span className="text-red-600">緊急事態</span>はありませんか？
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-alarm-warning-line",
                title: "資金繰りが急激に悪化",
                description: "支払いが困難になりそうで、すぐに対策が必要"
              },
              {
                icon: "ri-funds-line",
                title: "売上が急減している",
                description: "前年同月比で大幅な売上減少が続いている"
              },
              {
                icon: "ri-team-line",
                title: "重要な社員が退職予定",
                description: "キーパーソンの退職で業務に大きな影響が出そう"
              },
              {
                icon: "ri-bank-line",
                title: "銀行との交渉が必要",
                description: "融資の相談や返済条件の見直しを急いでいる"
              },
              {
                icon: "ri-file-warning-line",
                title: "税務調査の通知が来た",
                description: "税務署からの連絡で緊急に対応が必要"
              },
              {
                icon: "ri-error-warning-line",
                title: "経営判断を迫られている",
                description: "重要な決断を短期間で下さなければならない"
              }
            ].map((situation, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-500 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4">
                  <i className={`${situation.icon} text-red-500 text-4xl w-12 h-12 flex items-center justify-center flex-shrink-0`}></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{situation.title}</h3>
                    <p className="text-gray-700 leading-relaxed font-medium">{situation.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Immediate Response Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            <span className="text-red-600">緊急対応</span>の流れ
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-red-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">即日連絡</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                お問い合わせから<span className="font-bold text-red-600">24時間以内</span>に必ずご連絡いたします。緊急度に応じて当日対応も可能です。
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-discuss-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">緊急相談</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                <span className="font-bold text-orange-600">電話またはオンライン</span>で緊急相談を実施。現状把握と応急処置をご提案します。
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-green-600 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">迅速実行</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                <span className="font-bold text-green-600">最短翌日から</span>具体的な対策を実行。緊急事態の解決に向けて全力でサポートします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            <span className="text-blue-600">お問い合わせ方法</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-red-500">
              <div className="text-center mb-6">
                <i className="ri-phone-line text-red-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">電話でのお問い合わせ</h3>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-red-600 mb-4">03-1234-5678</p>
                <p className="text-gray-700 font-medium text-lg mb-4">
                  受付時間：平日 9:00〜18:00<br />
                  <span className="text-red-600 font-bold">緊急時は時間外も対応可能</span>
                </p>
                <a 
                  href="tel:03-1234-5678"
                  className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-lg font-bold text-lg transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
                >
                  📞 今すぐ電話する
                </a>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-blue-500">
              <div className="text-center mb-6">
                <i className="ri-mail-line text-blue-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">フォームでのお問い合わせ</h3>
              </div>
              <div className="text-center">
                <p className="text-gray-700 font-medium text-lg mb-4">
                  下記フォームからお問い合わせください<br />
                  <span className="text-blue-600 font-bold">24時間以内にご連絡</span>いたします
                </p>
                <a 
                  href="#urgent-form"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
                >
                  📝 フォームで問い合わせ
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Contact Form Section */}
      <section id="urgent-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-6 tracking-tight">
              緊急<span className="text-red-600">お問い合わせフォーム</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              緊急性の高いご相談は、こちらのフォームからお問い合わせください。<br />
              <span className="font-bold text-red-600">24時間以内</span>に必ずご連絡いたします。
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-xl p-8 border-t-8 border-red-500">
            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-500 text-2xl mr-3"></i>
                  <p className="text-green-700 font-semibold text-lg">
                    緊急お問い合わせを受け付けました。24時間以内にご連絡いたします。
                  </p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 text-2xl mr-3"></i>
                  <p className="text-red-700 font-semibold text-lg">
                    送信に失敗しました。お急ぎの場合は直接お電話ください。
                  </p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="urgent-contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-lg font-semibold text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
                    placeholder="example@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="urgency" className="block text-lg font-semibold text-gray-700 mb-2">
                    緊急度 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="今日中に対応が必要">今日中に対応が必要</option>
                    <option value="明日までに対応が必要">明日までに対応が必要</option>
                    <option value="今週中に対応が必要">今週中に対応が必要</option>
                    <option value="来週までに対応が必要">来週までに対応が必要</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="preferred_contact" className="block text-lg font-semibold text-gray-700 mb-2">
                    希望連絡方法 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="preferred_contact"
                    name="preferred_contact"
                    value={formData.preferred_contact}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="電話">電話</option>
                    <option value="メール">メール</option>
                    <option value="オンライン会議">オンライン会議</option>
                    <option value="訪問">訪問</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="preferred_time" className="block text-lg font-semibold text-gray-700 mb-2">
                  希望連絡時間帯
                </label>
                <input
                  type="text"
                  id="preferred_time"
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
                  placeholder="平日9:00-18:00、土日も可能など"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-2">
                  緊急事態の詳細 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg resize-none"
                  placeholder="現在の状況、緊急性の理由、求める支援内容などを詳しくお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? '送信中...' : '🚨 緊急お問い合わせを送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactNowPage;